import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { EnquiryType } from '@/content/enquiries'

import { useEffect, useId, useState } from 'react'
import { useRouter } from 'next/router'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { easeOutExpo } from '@/lib/motion'
import { enquiryTypes, isEnquiryType } from '@/content/enquiries'
import { formatPlate, getPhotograph, realms, byRealm } from '@/content/photographs'

type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    organisation: string
    type: EnquiryType | ''
    photograph: string
    message: string
    website: string
}

const INITIAL: FormValues = {
    name: '',
    email: '',
    organisation: '',
    type: '',
    photograph: '',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/

const validate = (values: FormValues): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (values.name.trim().length < 2) errors.name = 'Please tell us your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (!values.type) errors.type = 'Please choose what your enquiry is about.'
    if (values.message.trim().length < 10) errors.message = 'Please write a few words about your enquiry.'

    return errors
}

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    error?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, optional = false, error, children }): ReactNode => {
    return (
        <div className='group relative'>
            <label htmlFor={id} className='flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-fog transition-colors group-focus-within:text-bone'>
                {label}
                {optional && <span className='text-bone/30'>Optional</span>}
            </label>

            {children}

            <AnimatePresence initial={false}>
                {error && (
                    <motion.p
                        id={`${id}-error`}
                        role='alert'
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className='mt-2 text-[13px] text-[#f08a5d]'
                    >
                        {error}
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    )
}

const inputClass = 'mt-3 w-full border-0 border-b border-bone/25 bg-transparent pb-3 text-[19px] text-bone outline-none transition-colors placeholder:text-bone/25 focus:border-ember md:text-[21px]'

export const EnquiryForm: FC = (): ReactNode => {
    const router = useRouter()
    const baseId = useId()
    const [values, setValues] = useState<FormValues>(INITIAL)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [feedback, setFeedback] = useState('')

    useEffect(() => {
        if (!router.isReady) return

        const plate = router.query.plate
        const type = router.query.type

        setValues(current => ({
            ...current,
            photograph: typeof plate === 'string' && getPhotograph(plate) ? plate : current.photograph,
            type: isEnquiryType(type) ? type : current.type || (typeof plate === 'string' && getPhotograph(plate) ? 'prints' : current.type)
        }))
        // Read the deep link once, when the router is ready.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = event.target.value

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) {
            setErrors(current => {
                const next = { ...current }
                delete next[field]
                return next
            })
        }
    }

    const chooseType = (type: EnquiryType) => {
        setValues(current => ({ ...current, type }))

        if (errors.type) {
            setErrors(current => {
                const next = { ...current }
                delete next.type
                return next
            })
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            setStatus('idle')
            setFeedback('Please check the highlighted fields.')

            const first = Object.keys(found)[0]
            const element = document.getElementById(`${baseId}-${first}`)

            element?.focus()
            return
        }

        setStatus('submitting')
        setFeedback('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = (await response.json().catch(() => null)) as { ok?: boolean, message?: string, errors?: Record<string, string> } | null

            if (!response.ok || !data?.ok) {
                setErrors(data?.errors ?? {})
                setStatus('error')
                setFeedback(data?.message ?? 'Your message could not be sent just now. Please try again in a moment.')
                return
            }

            setStatus('success')
            setValues(INITIAL)
        }
        catch {
            setStatus('error')
            setFeedback('Your message could not be sent just now. Please check your connection and try again.')
        }
    }

    const fieldId = (field: string) => `${baseId}-${field}`
    const describedBy = (field: string) => (errors[field] ? `${fieldId(field)}-error` : undefined)

    return (
        <AnimatePresence mode='wait'>
            {status === 'success'
                ? (
                    <motion.div
                        key='success'
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: easeOutExpo }}
                        className='flex min-h-[60vh] flex-col justify-center border-t border-bone/15 pt-10'
                        role='status'
                    >
                        <p className='font-mono text-[10px] uppercase tracking-[0.22em] text-ember'>
                            Message received
                        </p>
                        <p className='mt-6 font-heading text-[clamp(40px,5vw,84px)] leading-[0.95] tracking-[-0.035em] text-bone'>
                            Thank you. <span className='italic'>It is on its way.</span>
                        </p>
                        <p className='mt-6 max-w-[44ch] text-[17px] leading-[1.65] text-bone/70'>
                            Your message has been delivered. Any reply will come to the email address you gave.
                        </p>
                        <button
                            type='button'
                            onClick={() => setStatus('idle')}
                            className='mt-10 self-start border-b border-bone/30 pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-bone transition-colors hover:border-ember hover:text-ember'
                        >
                            Send another message
                        </button>
                    </motion.div>
                )
                : (
                    <motion.form
                        key='form'
                        noValidate
                        onSubmit={onSubmit}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className='flex flex-col gap-10'
                        aria-describedby={feedback ? `${baseId}-feedback` : undefined}
                    >
                        <div className='grid gap-10 md:grid-cols-2 md:gap-8'>
                            <Field id={fieldId('name')} label='Your name' error={errors.name}>
                                <input
                                    id={fieldId('name')}
                                    name='name'
                                    type='text'
                                    autoComplete='name'
                                    value={values.name}
                                    onChange={update('name')}
                                    aria-invalid={Boolean(errors.name)}
                                    aria-describedby={describedBy('name')}
                                    className={inputClass}
                                    placeholder='Full name'
                                    maxLength={120}
                                    required
                                />
                            </Field>

                            <Field id={fieldId('email')} label='Email' error={errors.email}>
                                <input
                                    id={fieldId('email')}
                                    name='email'
                                    type='email'
                                    autoComplete='email'
                                    inputMode='email'
                                    value={values.email}
                                    onChange={update('email')}
                                    aria-invalid={Boolean(errors.email)}
                                    aria-describedby={describedBy('email')}
                                    className={inputClass}
                                    placeholder='name@example.com'
                                    maxLength={254}
                                    required
                                />
                            </Field>
                        </div>

                        <Field id={fieldId('organisation')} label='Organisation' optional>
                            <input
                                id={fieldId('organisation')}
                                name='organisation'
                                type='text'
                                autoComplete='organization'
                                value={values.organisation}
                                onChange={update('organisation')}
                                className={inputClass}
                                placeholder='Publication, school, gallery, company'
                                maxLength={160}
                            />
                        </Field>

                        <fieldset aria-describedby={describedBy('type')}>
                            <legend className='font-mono text-[10px] uppercase tracking-[0.22em] text-fog'>
                                What is it about?
                            </legend>

                            <div className='mt-4 flex flex-wrap gap-2'>
                                {enquiryTypes.map((type, index) => (
                                    <label key={type.id} title={type.hint} className='relative'>
                                        <input
                                            id={index === 0 ? fieldId('type') : undefined}
                                            type='radio'
                                            name='type'
                                            value={type.id}
                                            checked={values.type === type.id}
                                            onChange={() => chooseType(type.id)}
                                            className='peer sr-only'
                                        />
                                        <span className='block border border-bone/25 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-bone/80 transition-colors duration-300 hover:border-bone/60 hover:text-bone peer-checked:border-ember peer-checked:bg-ember peer-checked:text-peat peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-ember'>
                                            {type.label}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <AnimatePresence initial={false}>
                                {errors.type && (
                                    <motion.p
                                        id={`${fieldId('type')}-error`}
                                        role='alert'
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className='mt-2 text-[13px] text-[#f08a5d]'
                                    >
                                        {errors.type}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </fieldset>

                        <Field id={fieldId('photograph')} label='A particular photograph' optional error={errors.photograph}>
                            <select
                                id={fieldId('photograph')}
                                name='photograph'
                                value={values.photograph}
                                onChange={update('photograph')}
                                className={cn(inputClass, 'appearance-none pr-8')}
                                style={{
                                    backgroundImage: 'linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)',
                                    backgroundPosition: 'calc(100% - 14px) 55%, calc(100% - 9px) 55%',
                                    backgroundSize: '5px 5px, 5px 5px',
                                    backgroundRepeat: 'no-repeat'
                                }}
                            >
                                <option value='' className='bg-peat text-bone'>Not sure yet</option>
                                {realms.map(realm => (
                                    <optgroup key={realm.id} label={`${realm.numeral}. ${realm.name}`} className='bg-peat text-bone'>
                                        {byRealm(realm.id).map(photo => (
                                            <option key={photo.slug} value={photo.slug} className='bg-peat text-bone'>
                                                Pl. {formatPlate(photo.plate)}: {photo.title}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </Field>

                        <Field id={fieldId('message')} label='Message' error={errors.message}>
                            <textarea
                                id={fieldId('message')}
                                name='message'
                                rows={5}
                                value={values.message}
                                onChange={update('message')}
                                aria-invalid={Boolean(errors.message)}
                                aria-describedby={describedBy('message')}
                                className={cn(inputClass, 'resize-none leading-[1.5]')}
                                placeholder='Tell Arian what you have in mind.'
                                maxLength={4000}
                                required
                                data-lenis-prevent
                            />
                        </Field>

                        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                            <label htmlFor={fieldId('website')}>Leave this field empty</label>
                            <input
                                id={fieldId('website')}
                                name='website'
                                type='text'
                                tabIndex={-1}
                                autoComplete='off'
                                value={values.website}
                                onChange={update('website')}
                            />
                        </div>

                        <div className='flex flex-col gap-5 border-t border-bone/15 pt-8 md:flex-row md:items-center md:justify-between'>
                            <p
                                id={`${baseId}-feedback`}
                                aria-live='polite'
                                className={cn('text-[14px] leading-relaxed', status === 'error' || Object.keys(errors).length > 0 ? 'text-[#f08a5d]' : 'text-bone/55')}
                            >
                                {feedback || 'Your details are only used to answer this message.'}
                            </p>

                            <button
                                type='submit'
                                disabled={status === 'submitting'}
                                className='group relative inline-flex shrink-0 items-center justify-center gap-4 overflow-hidden bg-bone px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-peat transition-colors duration-500 hover:bg-ember disabled:cursor-wait disabled:opacity-80'
                            >
                                {status === 'submitting' ? 'Sending' : 'Send message'}
                                <span aria-hidden='true' className='transition-transform duration-500 group-hover:translate-x-1'>
                                    &rarr;
                                </span>

                                {status === 'submitting' && (
                                    <motion.span
                                        aria-hidden='true'
                                        className='absolute inset-x-0 bottom-0 h-[2px] origin-left bg-peat'
                                        initial={{ scaleX: 0 }}
                                        animate={{ scaleX: [0, 1] }}
                                        transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                                    />
                                )}
                            </button>
                        </div>
                    </motion.form>
                )}
        </AnimatePresence>
    )
}
